/**
 * The onboarding flow's spacing scale, in one place.
 *
 * Screens were each inventing their own margins, which read as sloppy the
 * moment you flip between them: the back chevron sat 4px inside the content
 * edge, headings started at three different heights, and option lists used
 * two different gaps. These constants are the vocabulary — a screen should
 * reach for one of them rather than a fresh `mt-7`.
 *
 * They're plain class strings so Tailwind's scanner still sees them.
 */

/** Horizontal gutter. Every edge in the flow — top bar, body, dock. */
export const GUTTER = "px-6";

/**
 * Cancels `GUTTER` for anything that has to reach the screen edges.
 *
 * The shell pads the whole content column, which is right for text and rows
 * and wrong for a full-bleed visual: a component that clips its own overflow
 * — the print conveyor, the story wheel — then clips at the gutter instead of
 * the viewport, and the design's "runs off the edge" reads as "stops short and
 * gets cut". Paired with `GUTTER` here so the two can't drift: change one and
 * the other is right next to it.
 */
export const BLEED = "-mx-6";

/** Space above a screen's heading block. */
export const HEADING_TOP = "pt-6";

/** Heading block → the first control under it. */
export const HEADING_GAP = "mt-6";

/** Between stacked options, city rows, cards — one rhythm for all lists. */
export const LIST_GAP = "gap-3";

/** A list → the reassurance line under it. */
export const FOOTNOTE_GAP = "mt-5";

/**
 * Shared geometry for anything row-shaped: option rows, the city search
 * field, city results. Keeping the radius and padding identical is what makes
 * the city screen read as the same product as the question screens.
 */
export const ROW_SHAPE = "rounded-2xl px-[18px] py-[15px]";
export const ROW_RADIUS = "rounded-2xl";

/**
 * Resting surface + hairline for an unselected row.
 *
 * The two themes get elevation from opposite properties, which is why the
 * same recipe cannot serve both. On the dark canvas a 4.5% white fill reads
 * as a surface lifted off the ground, and the row needs nothing else. On
 * white there is no "lighter than the canvas" to reach for, so light mode
 * steps *down* from it by the same amount instead. Dark's 4.5% white fill
 * lifts the row off the canvas by 1.090x; `#F7F4F2` sits 1.095x below white.
 * The same separation, mirrored — which is what makes an unselected row read
 * as a surface you can press rather than as empty page with a line round it.
 *
 * A white row and a hairline was the first attempt and it disappeared: on a
 * white canvas the only thing distinguishing the control from the background
 * was 1px of near-white, so five options read as four thin rules.
 *
 * Both the fill and the hairline are warm greys, not black at low alpha.
 * Black over white gives a cool grey that sits wrong against a palette of
 * orange, cream and film stock — the row reads as a stock component dropped
 * into someone else's design. These are the same values with the warmth left
 * in, and the edge stays a shade darker than the fill so it still draws the
 * boundary rather than dissolving into it.
 */
export const ROW_SURFACE =
  "bg-[#F7F4F2] dark:bg-white/[0.045] border-[#E7E0DA] dark:border-white/10";

/**
 * Selected row: brand tint + brand border, same border width as resting so
 * selecting never nudges the text.
 *
 * Light uses `primary-50` rather than a 10% overlay of `primary-500`. An
 * overlay of a saturated orange on white desaturates toward grey-pink; the
 * ramp's own tint stays warm.
 *
 * The weight comes from an inset ring doubling the border to 2px, not from a
 * coloured drop shadow. A glow under a selected row is the single clearest
 * tell of an unpolished interface: it reads as a highlight effect applied to
 * the row rather than as the row being chosen.
 *
 * Light takes `primary-100` and a `primary-600` edge rather than the 50/500
 * pair it started with. Against a white row, `primary-50` is a 1.09 luminance
 * step — technically a tint, visually nothing, so a chosen row read as barely
 * marked. `primary-100` is 1.22 and unmistakable, and body text on it still
 * runs 14.6:1. The deeper edge keeps the border reading as a deliberate
 * outline once the fill inside it has colour of its own.
 */
export const ROW_SURFACE_SELECTED =
  "bg-primary-100 dark:bg-primary-500/10 border-primary-600 dark:border-primary-500 " +
  "shadow-[inset_0_0_0_1px_#D9521A] dark:shadow-none";

/**
 * Every row carries a border at all times and only changes its colour, so
 * selecting one never nudges the text by half a pixel.
 *
 * One pixel, not 1.5. At 1.5 the hairline is thick enough to read as drawn-on
 * rather than as an edge, which on a 16px radius looks like a beginner's
 * outline. The selected row gets its extra weight from an inset ring instead
 * (see `ROW_SURFACE_SELECTED`) — a shadow, so it costs no layout and the text
 * still cannot move.
 */
export const ROW_BORDER = "border";
